import api from "../api/"
import config from "@/config/"
import { i18n } from "@/locales/setup.js"

export default {
	async logIn({ commit, dispatch, rootState }, payload) {
		const { tenantId } = rootState
		const clientId = config.CLIENT_ID
		const res = await api.logIn({ clientId, tenantId, ...payload })

		if (!res.data || !res.data.token || res.data.token.error) {
			const tokenError =
				res.data && res.data.token && res.data.token.error
			const errorMessage =
				tokenError === "locked_out"
					? "Your account is locked out due to multiple invalid login attempts"
					: "Email or password is not correct"
			dispatch(
				"addMessage",
				{
					message: { type: "error", text: errorMessage }
				},
				{ root: true }
			)
			return Promise.resolve()
		} else {
			window.TK = res.data.token.accessToken
			commit("SET_TENANT_ID", tenantId, { root: true })
			return Promise.resolve()
		}
	},

	async newPassword({ dispatch }, payload) {
		const res = await api.newPassword({ ...payload })

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			return Promise.resolve(false)
		} else {
			return Promise.resolve(true)
		}
	},

	async forgotPassword({ dispatch, rootState }, payload) {
		const { tenantId } = rootState
		const clientId = config.CLIENT_ID
		// sendNotificationCommand.emailMessage must be present, even empty.
		//
		// Auth commit 422a6219 dropped the `EmailMessage = new EmailMessage()` initialiser
		// from ForgotPasswordAsync's default SendNotificationCommand, then dereferences
		// `.EmailMessage.To` a few lines later. Sending nothing here means Auth builds the
		// command itself with a null EmailMessage and throws NullReferenceException.
		//
		// The failure is invisible in deployed environments: the gateway's
		// CoverGoAuthService.ForgotPasswordAsync has no EnsureSuccessStatusCode, so it
		// deserializes the empty body of Auth's 500 into null and the mutation returns
		// {"data":{"forgotPassword":null}} with no errors array - which surfaces here as a
		// generic error with no cause. Locally the body is the developer exception page
		// instead, so it fails as a JSON parse error. Same bug, two faces.
		//
		// Empty is deliberate: Auth fills To/From/subject/body itself. We send no content,
		// so this does not reintroduce the client-controlled email content that
		// DisAllowSendNotificationParameters (and forgotPassword2) exist to prevent.
		//
		// Remove once Auth restores the guard - it is still missing on master.
		const res = await api.forgotPassword({
			tenantId,
			forgotPasswordInput: {
				clientId,
				...payload,
				sendNotificationCommand: { emailMessage: {} }
			}
		})

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			return Promise.resolve(false)
		} else {
			return Promise.resolve(true)
		}
	},

	async getPasswordValidators(_, tenantId) {
		const response = await api.passwordValidators({
			tenantId
		})

		const rules = response.data.passwordValidators

		// Define the desired order of password rules
		const orderedKeys = [
			"requireLength",
			"requireUppercase",
			"requireLowercase",
			"requireDigit",
			"requireNonAlphanumeric",
			"requireUniqueChars",
			"requireConfirmPhoneNumber",
			"requireConfirmedEmail"
		]

		return orderedKeys
			.map(key => {
				const value = rules[key]

				if (key === "requireLength" && value !== false)
					return i18n.messages[i18n.locale].passwordRules.requireLength.replace(
						"{value}",
						value
					)
				if (key === "requireUppercase" && value)
					return i18n.messages[i18n.locale].passwordRules.requireUppercase
				if (key === "requireLowercase" && value)
					return i18n.messages[i18n.locale].passwordRules.requireLowercase
				if (key === "requireDigit" && value)
					return i18n.messages[i18n.locale].passwordRules.requireDigit
				if (key === "requireNonAlphanumeric" && value)
					return i18n.messages[i18n.locale].passwordRules.requireNonAlphanumeric
				if (key === "requireUniqueChars" && value > 0)
					// we don't need to show this rule on the UI
					return undefined
				if (key === "requireConfirmPhoneNumber" && value)
					return i18n.messages[i18n.locale].passwordRules
						.requireConfirmPhoneNumber
				if (key === "requireConfirmedEmail" && value)
					return i18n.messages[i18n.locale].passwordRules.requireConfirmedEmail
			})
			.filter(rule => rule !== undefined)
	},

	logOut({ commit }, tenantId) {
		commit("UPDATE_TOKEN", null)
		// Hard redirect that will flush store
		window.location = `/${tenantId}/login`
	}
}
